import React, { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faArrowUpRightFromSquare,
  faCalendarDays,
  faClock,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import BlogData from "../../JsonFiles/Blogs.json";
import "./Blog.css";

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  return fallback;
};

const getArticleId = (article, fallback) =>
  asText(article?.id ?? article?.slug ?? fallback);

const getTitle = (article) =>
  asText(article?.title ?? article?.name, "Axiomos insight");

const getCategory = (article) =>
  asText(
    article?.industry ?? article?.category ?? article?.topic ?? article?.type,
    "Technology",
  );

const getImage = (article) =>
  asText(
    article?.photo ??
      article?.image ??
      article?.coverImage ??
      article?.heroImage ??
      article?.thumbnail,
  );

const getDescription = (article) =>
  asText(
    article?.descriptionsmall ??
      article?.shortDescription ??
      article?.summary ??
      article?.excerpt ??
      article?.description ??
      article?.overview,
    "Explore practical perspectives from Axiomos on technology, product engineering, and digital transformation.",
  );

const normalizeTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        asText(item?.title ?? item?.name ?? item?.label ?? item),
      )
      .filter(Boolean)
      .slice(0, 6);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  return [];
};

const normalizeParagraphs = (article) => {
  const value =
    article?.content ??
    article?.body ??
    article?.articleContent ??
    article?.details ??
    article?.description;

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        asText(item?.paragraph ?? item?.text ?? item?.content ?? item),
      )
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }

  return [getDescription(article)];
};

const getArticleDate = (article) => {
  const rawDate =
    article?.publishedAt ??
    article?.publishDate ??
    article?.date ??
    article?.createdAt;

  if (!rawDate) return "";

  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) return asText(rawDate);

  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
};

const getReadingTime = (article) => {
  const providedTime = asText(article?.readTime ?? article?.readingTime);
  if (providedTime) {
    return /read/i.test(providedTime) ? providedTime : `${providedTime} read`;
  }

  const wordCount = normalizeParagraphs(article).join(" ").split(/\s+/).length;
  return `${Math.max(1, Math.ceil(wordCount / 200))} min read`;
};

const getAuthor = (article) =>
  asText(
    article?.author?.name ?? article?.author ?? article?.writtenBy,
    "Axiomos Insights",
  );

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

const ArticleImage = ({ article, eager = false }) => {
  const [failed, setFailed] = useState(false);
  const image = getImage(article);
  const title = getTitle(article);
  const fallback = title.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "AX";

  if (!image || failed) {
    return (
      <div className="blog-pages__image-fallback" aria-hidden="true">
        {fallback}
      </div>
    );
  }

  return (
    <img
      src={image}
      alt={title}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const ArticleCard = ({ article, index }) => {
  const articleId = getArticleId(article, index + 1);
  const title = getTitle(article);
  const date = getArticleDate(article);

  return (
    <article className="blog-pages__card">
      <ScrollLink
        to={`/blog/${encodeURIComponent(articleId)}`}
        className="blog-pages__card-link"
        aria-label={`Read ${title}`}
      >
        <div className="blog-pages__card-media">
          <ArticleImage article={article} eager={index === 0} />
          <div className="blog-pages__card-overlay" aria-hidden="true" />
          <span className="blog-pages__card-category">
            {getCategory(article)}
          </span>
          <span className="blog-pages__card-number" aria-hidden="true">
            Insight / {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        <div className="blog-pages__card-content">
          <div className="blog-pages__meta">
            {date && (
              <span>
                <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                {date}
              </span>
            )}
            <span>
              <FontAwesomeIcon icon={faClock} aria-hidden="true" />
              {getReadingTime(article)}
            </span>
          </div>

          <h2>{title}</h2>
          <p>{getDescription(article)}</p>

          <span className="blog-pages__card-action">
            Read insight
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </span>
        </div>
      </ScrollLink>
    </article>
  );
};

export function BlogListPages() {
  const articles = Array.isArray(BlogData) ? BlogData : [];
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(
    () => [
      "All",
      ...Array.from(new Set(articles.map(getCategory).filter(Boolean))),
    ],
    [articles],
  );

  const filteredArticles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return articles.filter((article) => {
      const categoryMatches =
        activeCategory === "All" || getCategory(article) === activeCategory;
      const searchableText = [
        getTitle(article),
        getDescription(article),
        getCategory(article),
        ...normalizeTags(article?.tags ?? article?.technologies),
      ]
        .join(" ")
        .toLowerCase();

      return categoryMatches && searchableText.includes(normalizedQuery);
    });
  }, [activeCategory, articles, query]);

  const clearFilters = () => {
    setQuery("");
    setActiveCategory("All");
  };

  return (
    <main className="blog-pages">
      <section className="blog-pages__hero" aria-labelledby="blog-list-title">
        <div className="blog-pages__hero-decoration" aria-hidden="true" />
        <div className="blog-pages__container blog-pages__hero-inner">
          <div>
            <p className="blog-pages__eyebrow">
              <span aria-hidden="true" />
              Ideas &amp; insights
            </p>
            <h1 id="blog-list-title">
              Thinking that moves
              <span> business forward.</span>
            </h1>
          </div>

          <div className="blog-pages__hero-copy">
            <p>
              Practical perspectives on digital transformation, product
              engineering, technology strategy, and the decisions behind
              better software.
            </p>
            <span>{String(articles.length).padStart(2, "0")} published insights</span>
          </div>
        </div>
      </section>

      <section className="blog-pages__library" aria-label="Axiomos articles">
        <div className="blog-pages__container">
          <div className="blog-pages__toolbar">
            <label className="blog-pages__search">
              <span className="blog-pages__sr-only">Search articles</span>
              <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search insights"
              />
            </label>

            <div className="blog-pages__categories" aria-label="Article categories">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={activeCategory === category ? "is-active" : ""}
                  onClick={() => setActiveCategory(category)}
                  aria-pressed={activeCategory === category}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="blog-pages__results-heading">
            <p>
              Showing <strong>{filteredArticles.length}</strong>{" "}
              {filteredArticles.length === 1 ? "article" : "articles"}
            </p>
          </div>

          {filteredArticles.length > 0 ? (
            <div className="blog-pages__grid">
              {filteredArticles.map((article, index) => (
                <ArticleCard
                  key={getArticleId(article, index)}
                  article={article}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="blog-pages__empty" role="status">
              <span aria-hidden="true">AX</span>
              <h2>No matching insights found</h2>
              <p>Try another keyword or reset the selected category.</p>
              <button type="button" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export function BlogsPage() {
  const { id } = useParams();
  const articles = Array.isArray(BlogData) ? BlogData : [];
  const articleIndex = articles.findIndex(
    (item, index) => getArticleId(item, index + 1) === String(id),
  );
  const article = articles[articleIndex];

  if (!article) {
    return (
      <main className="blog-pages blog-pages--detail">
        <section className="blog-pages__not-found">
          <span aria-hidden="true">404</span>
          <h1>We couldn’t find that insight.</h1>
          <p>The article may have moved or the link may be incomplete.</p>
          <ScrollLink to="/blog">
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            Back to all insights
          </ScrollLink>
        </section>
      </main>
    );
  }

  const title = getTitle(article);
  const date = getArticleDate(article);
  const paragraphs = normalizeParagraphs(article);
  const tags = normalizeTags(
    article?.tags ?? article?.technologies ?? article?.topics,
  );
  const externalLink = asText(article?.link ?? article?.sourceUrl);
  const relatedArticles = articles
    .filter((item, index) => {
      if (index === articleIndex) return false;
      return getCategory(item) === getCategory(article);
    })
    .slice(0, 3);

  return (
    <main className="blog-pages blog-pages--detail">
      <article>
        <header className="blog-pages__article-header">
          <div className="blog-pages__container blog-pages__article-heading">
            <ScrollLink to="/blog" className="blog-pages__back-link">
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
              All insights
            </ScrollLink>

            <p className="blog-pages__article-category">{getCategory(article)}</p>
            <h1>{title}</h1>
            <p className="blog-pages__article-summary">
              {getDescription(article)}
            </p>

            <div className="blog-pages__article-meta">
              <span>By {getAuthor(article)}</span>
              {date && (
                <span>
                  <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                  {date}
                </span>
              )}
              <span>
                <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                {getReadingTime(article)}
              </span>
            </div>
          </div>
        </header>

        <div className="blog-pages__container blog-pages__article-hero">
          <ArticleImage article={article} eager />
        </div>

        <div className="blog-pages__container blog-pages__article-layout">
          <div className="blog-pages__article-body">
            {paragraphs.map((paragraph, index) => (
              <p key={`${paragraph.slice(0, 24)}-${index}`}>{paragraph}</p>
            ))}

            {externalLink && (
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-pages__source-link"
              >
                View original source
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  aria-hidden="true"
                />
              </a>
            )}
          </div>

          <aside className="blog-pages__article-sidebar">
            <div className="blog-pages__sidebar-card">
              <span>Article</span>
              <strong>{getCategory(article)}</strong>
              <p>Written by {getAuthor(article)}</p>
            </div>

            {tags.length > 0 && (
              <div className="blog-pages__sidebar-tags">
                <p>Topics covered</p>
                <ul>
                  {tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </article>

      <section className="blog-pages__article-cta" aria-labelledby="blog-cta-title">
        <div className="blog-pages__container">
          <div>
            <p>Have a technology challenge?</p>
            <h2 id="blog-cta-title">Let’s turn the insight into action.</h2>
          </div>
          <ScrollLink to="/contact">
            Start a conversation
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </ScrollLink>
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="blog-pages__related" aria-labelledby="related-title">
          <div className="blog-pages__container">
            <div className="blog-pages__related-heading">
              <div>
                <p>Continue exploring</p>
                <h2 id="related-title">Related insights</h2>
              </div>
              <ScrollLink to="/blog">
                View all
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </ScrollLink>
            </div>

            <div className="blog-pages__grid">
              {relatedArticles.map((item, index) => (
                <ArticleCard
                  key={getArticleId(item, index)}
                  article={item}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
